import { RefObject, useEffect, useLayoutEffect } from 'react';

type PlaybackSnapshot = {
  currentTime: number;
  playing: boolean;
};

const playbackByUrl = new Map<string, PlaybackSnapshot>();

const saveVideoPlayback = (videoUrl: string, video: HTMLVideoElement) => {
  if (!videoUrl || video.readyState < HTMLMediaElement.HAVE_METADATA) {
    return;
  }

  playbackByUrl.set(videoUrl, {
    currentTime: video.currentTime,
    playing: !video.paused && !video.ended,
  });
};

const hasPlaybackSnapshot = (videoUrl: string) =>
  Boolean(videoUrl && playbackByUrl.has(videoUrl));

const seekAndResume = (
  video: HTMLVideoElement,
  snapshot: PlaybackSnapshot,
  shouldPlay: boolean,
) => {
  try {
    if (Number.isFinite(snapshot.currentTime)) {
      video.currentTime = snapshot.currentTime;
    }
  } catch {
    // Seek can fail before the element is fully ready.
  }

  if (shouldPlay) {
    void video.play().catch(() => undefined);
    return;
  }

  video.pause();
};

const restoreVideoPlayback = (
  videoUrl: string,
  video: HTMLVideoElement,
  shouldPlay: boolean,
): boolean => {
  const snapshot = playbackByUrl.get(videoUrl);
  if (!snapshot) {
    return false;
  }

  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
    seekAndResume(video, snapshot, shouldPlay);
    return true;
  }

  const onLoadedMetadata = () => {
    video.removeEventListener('loadedmetadata', onLoadedMetadata);
    seekAndResume(video, snapshot, shouldPlay);
  };

  video.addEventListener('loadedmetadata', onLoadedMetadata);
  return true;
};

/**
 * Keeps playback position across router.refresh() when the video URL is unchanged.
 */
const useVideoPlaybackPreserve = (
  videoRef: RefObject<HTMLVideoElement | null>,
  videoUrl: string,
  shouldPlay: boolean,
) => {
  const skipRevealOnRestore = hasPlaybackSnapshot(videoUrl);

  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) {
      return;
    }

    restoreVideoPlayback(videoUrl, video, shouldPlay);
  }, [shouldPlay, videoRef, videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) {
      return;
    }

    let lastSavedAt = 0;
    const onTimeUpdate = () => {
      const now = Date.now();
      if (now - lastSavedAt < 250) {
        return;
      }

      lastSavedAt = now;
      saveVideoPlayback(videoUrl, video);
    };

    video.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      saveVideoPlayback(videoUrl, video);
    };
  }, [videoRef, videoUrl]);

  return { skipRevealOnRestore };
};

export { hasPlaybackSnapshot, useVideoPlaybackPreserve };
